import { useEffect, useState } from "react";
import type { MiniAppRoute, MiniAppSessionResponse } from "../api/types.js";
import { useMessages } from "../i18n/LocaleProvider.js";
import { segmentLabel } from "../i18n/messages.js";
import { useLocale } from "../i18n/LocaleProvider.js";
import { useCart } from "../hooks/useCart.js";
import { useCheckout } from "../hooks/useCheckout.js";
import { bootstrapTelegramWebApp } from "../lib/telegram.js";
import { BottomNav } from "./BottomNav.js";
import { CartView } from "./cart/CartView.js";
import { AllCategoriesView } from "./catalog/AllCategoriesView.js";
import { CatalogView } from "./catalog/CatalogView.js";
import { useCatalogCategories } from "../hooks/useCatalogCategories.js";
import { CheckoutView } from "./checkout/CheckoutView.js";
import { HomeView, type CatalogFilter } from "./home/HomeView.js";
import { ManagerCabinetView } from "./manager/ManagerCabinetView.js";
import { ProfileView } from "./profile/ProfileView.js";

interface AppShellProps {
  displayName: string;
  segment?: string | null;
  session: MiniAppSessionResponse;
}

export function AppShell({ displayName, segment, session }: AppShellProps) {
  const messages = useMessages();
  const { locale } = useLocale();
  const [route, setRoute] = useState<MiniAppRoute>("home");
  const [catalogCategory, setCatalogCategory] = useState<string | null>(null);
  const [catalogCollection, setCatalogCollection] = useState<string | null>(null);
  const [catalogMountKey, setCatalogMountKey] = useState(0);
  const [catalogCommerce, setCatalogCommerce] = useState({
    cartDisabled: false,
    checkoutDisabled: false,
    degraded: false,
    messageUk: "",
  });
  const cartState = useCart({ autoLoad: true });
  const checkoutState = useCheckout();
  const categoriesState = useCatalogCategories();

  const degradedMessage =
    catalogCommerce.degraded && catalogCommerce.messageUk
      ? catalogCommerce.messageUk
      : null;

  useEffect(() => {
    bootstrapTelegramWebApp();
  }, []);

  function openCatalog(filter: CatalogFilter = {}) {
    setCatalogCategory(filter.category ?? null);
    setCatalogCollection(filter.collection ?? null);
    setCatalogMountKey((value) => value + 1);
    setRoute("catalog");
  }

  const routeTitle =
    route === "home"
      ? messages.home.greeting(displayName)
      : route === "catalog"
        ? messages.catalog.title
        : route === "all_categories"
          ? messages.catalog.allCategories
        : route === "cart" || route === "checkout"
          ? messages.cart.title
          : route === "manager"
            ? messages.manager.title
          : route === "profile"
            ? messages.profile.title
            : messages.brand.name;

  return (
    <div className="app-shell flex min-h-screen flex-col bg-[var(--tg-theme-bg-color,#ffffff)] text-[var(--tg-theme-text-color,#000000)]">
      <header className="sticky top-0 z-20 border-b border-black/10 bg-[var(--tg-theme-bg-color,#ffffff)]/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-300">
              {messages.brand.name}
            </p>
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] opacity-60">
              {messages.brand.international}
            </p>
            <h1 className="truncate text-lg font-semibold leading-tight">{routeTitle}</h1>
          </div>
          {segment ? (
            <span className="shrink-0 rounded-full bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 py-1 text-xs font-medium">
              {segmentLabel(segment, locale) ?? segment}
            </span>
          ) : null}
          {session.manager_pricing_access ? (
            <button
              type="button"
              onClick={() => setRoute("manager")}
              className="shrink-0 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-800"
            >
              {messages.manager.entry}
            </button>
          ) : null}
        </div>
      </header>

      <main className="app-main-scroll mx-auto flex w-full flex-1 flex-col">
        {route === "home" ? (
          <HomeView
            cartCount={cartState.itemCount}
            onOpenCatalog={openCatalog}
            onOpenAllCategories={() => setRoute("all_categories")}
            onOpenCart={() => setRoute("cart")}
            onAddToCart={async (productId) => {
              if (catalogCommerce.cartDisabled) return;
              await cartState.addItem(productId, 1);
            }}
            cartMutating={cartState.mutating}
            catalogReadOnly={catalogCommerce.cartDisabled}
            onCatalogCommerceChange={setCatalogCommerce}
          />
        ) : null}

        {route === "all_categories" && categoriesState.data ? (
          <AllCategoriesView
            categories={
              categoriesState.data.categories ??
              [...categoriesState.data.primary, ...categoriesState.data.overflow]
            }
            collections={categoriesState.data.collections}
            onBack={() => setRoute("home")}
            onSelectCategory={(category) => {
              openCatalog({ category });
            }}
            onSelectCollection={(collection) => {
              openCatalog({ collection });
            }}
            showStagingCounts
          />
        ) : null}

        {route === "catalog" ? (
          <CatalogView
            key={`catalog-${catalogMountKey}`}
            initialCategory={catalogCategory}
            initialCollection={catalogCollection}
            onAddToCart={async (productId, qty = 1) => {
              if (catalogCommerce.cartDisabled) return;
              await cartState.addItem(productId, qty);
            }}
            cartMutating={cartState.mutating}
            catalogReadOnly={catalogCommerce.cartDisabled}
            onOpenAllCategories={() => setRoute("all_categories")}
            onCategoryChange={(id) => {
              setCatalogCategory(id);
              setCatalogCollection(null);
            }}
          />
        ) : null}

        {route === "cart" ? (
          <CartView
            cartState={cartState}
            onBrowseCatalog={() => openCatalog()}
            onCheckout={() => {
              if (catalogCommerce.checkoutDisabled) return;
              setRoute("checkout");
            }}
            checkoutDisabled={catalogCommerce.checkoutDisabled}
            degradedMessage={
              catalogCommerce.checkoutDisabled ? degradedMessage : null
            }
          />
        ) : null}

        {route === "checkout" ? (
          catalogCommerce.checkoutDisabled ? (
            <section className="flex flex-1 flex-col gap-4 p-4">
              <p className="text-sm opacity-80">
                Каталог тимчасово відкритий у резервному режимі. Замовлення оформлюються після
                відновлення сервісу.
              </p>
              <button
                type="button"
                className="self-start min-h-11 text-sm font-medium text-[var(--tg-theme-link-color,#3390ec)]"
                onClick={() => setRoute("cart")}
              >
                {messages.checkout.backToCart}
              </button>
            </section>
          ) : (
          <CheckoutView
            session={session}
            cartState={cartState}
            checkoutState={checkoutState}
            onBackToCart={() => setRoute("cart")}
            onBrowseCatalog={() => {
              checkoutState.reset();
              openCatalog();
            }}
          />
          )
        ) : null}

        {route === "profile" ? <ProfileView session={session} /> : null}

        {route === "manager" ? (
          <ManagerCabinetView onBack={() => setRoute("home")} />
        ) : null}
      </main>

      <BottomNav
        active={route === "checkout" ? "cart" : route === "all_categories" ? "catalog" : route}
        onNavigate={setRoute}
        cartCount={cartState.itemCount}
      />
    </div>
  );
}
